import React from "react";
import { Navigate } from "react-router";
import { Callout, Page, QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { FORBIDDEN_ROUTE, PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { useGetOrganizationUsageQuery } from "Services";
import Entitlements from "../../components/Entitlements/Entitlements";
import Header from "../../components/Header/Header";
import ManageSiteKeys from "../../components/ManageSiteKeys/ManageSiteKeys";
import { useGetCliUsageQuery } from "../../services/licensing.api";
import styles from "./cliCapacity.module.scss";

const CliCapacity = (): React.JSX.Element => {
  const currentOrganization = useCurrentOrganization();
  const canViewCliCapacity = useAllowedTo({
    permissions: [PERMISSION.VIEW_CLI_SITE_KEY_ALLOCATIONS],
  });
  const canManageCliCapacity = useAllowedTo({
    permissions: [PERMISSION.MODIFY_CLI_SITE_KEY_ALLOCATIONS],
  });

  const { data, isError, isFetching, isLoading, error } =
    useGetOrganizationUsageQuery(
      {
        organizationId: currentOrganization?.id || "",
      },
      { skip: !currentOrganization || !canViewCliCapacity },
    );
  const { data: cliUsage } = useGetCliUsageQuery(
    {
      organizationId: currentOrganization?.id || "",
      sortBy: "allocation",
      offset: 0,
      limit: 10,
    },
    { skip: !currentOrganization },
  );

  if (!canViewCliCapacity) {
    return <Navigate to={`/${FORBIDDEN_ROUTE}`} replace />;
  }

  return (
    <Page.Root>
      <Page.Header
        title={`${canManageCliCapacity ? "Manage" : "Review"} CLI capacity`}
      />
      <QueryResult
        status={getQueryStatus({
          data,
          isError,
          isFetching,
          isLoading,
          error,
        })}
      >
        <Page.Body>
          <div className={styles.stack}>
            <div className={styles.overview}>
              {cliUsage?.has_site_keys_with_allocation_zero &&
                canManageCliCapacity && (
                  <Callout
                    type="warning"
                    title="Monthly allocation for one or more site keys has been set to 0 and cannot be used for uploads"
                  >
                    Increase their allocation or delete them if they are no
                    longer needed.
                  </Callout>
                )}
              <Header data={data} />
              <Entitlements data={data?.entitlements} />
            </div>
            <ManageSiteKeys />
          </div>
        </Page.Body>
      </QueryResult>
    </Page.Root>
  );
};

export default CliCapacity;
